import React from "react";
import { money } from "../lib/api";
import modernHeader from "../assets/tpl/modern_header.jpg";
import modernBar from "../assets/tpl/modern_bar.jpg";
import modernPaper from "../assets/tpl/modern_paper.jpg";
import premiumHeader from "../assets/tpl/premium_header.jpg";
import premiumBar from "../assets/tpl/premium_bar.jpg";
import premiumPaper from "../assets/tpl/premium_paper.jpg";
import signatureHeader from "../assets/tpl/signature_header.jpg";
import signatureBar from "../assets/tpl/signature_bar.jpg";
import signaturePaper from "../assets/tpl/signature_paper.jpg";

// Palettes mirror backend/pdf_gen THEMES so the on-screen preview matches the PDF.
export const THEMES = {
  basic:     { bg: "#FBF8F2", ink: "#1C1915", primary: "#0B2233", accent: "#C9A24B", pale: "#ECD9A8", gray: "#5C564E", hair: "#E4DCCB", row: "#EFE6D3", barText: "#0B2233", style: "band" },
  modern:    { bg: "#F3EBDC", ink: "#1C1915", primary: "#0B1F3A", accent: "#A8873A", pale: "#E9D9AE", gray: "#6B5E45", hair: "#C9AE6A", row: "#C9AE6A", barText: "#0B1F3A", style: "image", img: { header: modernHeader, bar: modernBar, paper: modernPaper } },
  premium:   { bg: "#ECE7DD", ink: "#2B2B28", primary: "#0F3B2E", accent: "#77776F", pale: "#D8D8D2", gray: "#5F5F5A", hair: "#A9A9A3", row: "#A9A9A3", barText: "#0B1F3A", style: "image", img: { header: premiumHeader, bar: premiumBar, paper: premiumPaper } },
  signature: { bg: "#F2F1EE", ink: "#111111", primary: "#111111", accent: "#8C8C8C", pale: "#DADADA", gray: "#4A4A4A", hair: "#9A9A9A", row: "#9A9A9A", barText: "#FFFFFF", style: "image", img: { header: signatureHeader, bar: signatureBar, paper: signaturePaper } },
};

const rgba = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

// On-screen render of the quote paper (mirrors the selected PDF template).
export const QuotePreview = ({ data }) => {
  const {
    company_name,
    company_phone,
    company_address,
    logo,
    quote_number,
    date,
    customer_name,
    customer_address,
    customer_phone,
    customer_email,
    job_title,
    lines = [],
    note,
    tax_rate,
    is_free,
    template,
  } = data;

  const t = THEMES[template] || THEMES.basic;
  const band = t.style === "band";
  const img = t.style === "image" ? t.img : null;
  const serifLabel = img ? "font-serif" : "";

  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const hasTax = tax_rate !== null && tax_rate !== "" && Number(tax_rate) > 0;
  const taxAmt = hasTax ? (subtotal * Number(tax_rate)) / 100 : 0;
  const total = subtotal + taxAmt;

  const companyName = (company_name || "Your Company").toUpperCase();

  return (
    <div
      data-testid="quote-preview"
      className="relative rounded-sm shadow-xl border mx-auto overflow-hidden"
      style={{ aspectRatio: "8.5 / 11", width: "100%", maxWidth: 560, backgroundColor: t.bg, borderColor: t.hair, ...(img ? { backgroundImage: `url(${img.paper})`, backgroundSize: "100% 100%" } : {}) }}
    >
      <div className="relative h-full w-full p-[7%] flex flex-col overflow-hidden">
        {is_free && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10">
            <span
              className="font-serif font-bold tracking-[0.2em] select-none"
              style={{ color: rgba(t.accent, 0.18), fontSize: "clamp(48px,11vw,110px)", transform: "rotate(-30deg)" }}
            >
              SAMPLE
            </span>
          </div>
        )}

        {/* header */}
        {img ? (
          <>
            <img src={img.header} alt="" className="-mx-[7%] -mt-[7%] max-w-none" style={{ width: "114%" }} data-testid="template-header-art" />
            <div className="mt-[4%] flex items-start justify-between gap-3">
              <h2 className="font-serif font-bold uppercase leading-none" style={{ color: t.primary, fontSize: "clamp(16px,3vw,26px)" }}>
                {companyName}
              </h2>
              <div className="shrink-0 flex items-center justify-center" style={{ width: "11%", aspectRatio: "1" }}>
                {logo ? (
                  <img src={logo} alt="logo" className="w-full h-full object-contain" />
                ) : (
                  <svg viewBox="0 0 40 40" className="w-full h-full">
                    <polygon points="20,6 34,32 6,32" fill="none" stroke={t.accent} strokeWidth="1.5" />
                    <polygon points="20,14 28,30 12,30" fill={t.primary} />
                  </svg>
                )}
              </div>
            </div>
          </>
        ) : band ? (
          <>
            <div
              className="-mx-[7%] -mt-[7%] px-[7%] py-[6%] flex items-center justify-between gap-3"
              style={{ backgroundColor: t.primary }}
            >
              <h2 className="font-serif font-bold uppercase leading-none" style={{ color: "#FFFFFF", fontSize: "clamp(18px,3.4vw,30px)" }}>
                {companyName}
              </h2>
              <div className="shrink-0 flex items-center justify-center border" style={{ width: "13%", aspectRatio: "1", borderColor: t.accent }}>
                {logo ? (
                  <img src={logo} alt="logo" className="w-full h-full object-contain p-1" />
                ) : (
                  <svg viewBox="0 0 40 40" className="w-2/3 h-2/3">
                    <polygon points="20,8 32,30 8,30" fill="#FFFFFF" />
                  </svg>
                )}
              </div>
            </div>
            <div className="-mx-[7%]" style={{ height: 5, backgroundColor: t.accent }} />
          </>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-serif font-bold uppercase leading-none" style={{ color: t.primary, fontSize: "clamp(18px,3.4vw,30px)" }}>
                {companyName}
              </h2>
              <div className="shrink-0 flex items-center justify-center border" style={{ width: "13%", aspectRatio: "1", borderColor: t.accent }}>
                {logo ? (
                  <img src={logo} alt="logo" className="w-full h-full object-contain p-1" />
                ) : (
                  <svg viewBox="0 0 40 40" className="w-2/3 h-2/3">
                    <polygon points="20,8 32,30 8,30" fill={t.primary} />
                  </svg>
                )}
              </div>
            </div>
            <div className="mt-2 h-px w-full" style={{ backgroundColor: t.accent }} />
          </>
        )}

        {/* company contact details */}
        {(company_phone || company_address) && (
          <div className="mt-1.5 text-[2vw] sm:text-[10px] leading-snug" style={{ color: t.gray }}>
            {company_phone && <div>{company_phone}</div>}
            {company_address && <div>{company_address}</div>}
          </div>
        )}

        {/* meta */}
        <div className={`mt-3 flex items-center justify-between ${img ? "text-[2.6vw] sm:text-[14px]" : "text-[2.1vw] sm:text-[11px]"}`} style={{ color: t.primary }}>
          <span>
            <b className={`tracking-wide ${serifLabel}`} style={img ? { color: t.accent } : undefined}>QUOTE:</b> {quote_number || "Q-0000"}
          </span>
          <span>
            <b className={`tracking-wide ${serifLabel}`} style={img ? { color: t.accent } : undefined}>DATE:</b> {date || "—"}
          </span>
        </div>

        {/* customer / job */}
        <div className="mt-4 grid grid-cols-2 gap-4" style={{ fontSize: "clamp(9px,2vw,12px)" }}>
          <div>
            {customer_name && (
              <>
                <div className="font-bold tracking-wide mb-1" style={{ color: t.primary }}>CUSTOMER</div>
                <div style={{ color: t.ink }} className="leading-snug">
                  <div>{customer_name}</div>
                  {customer_address && <div>{customer_address}</div>}
                  {customer_phone && <div>{customer_phone}</div>}
                  {customer_email && <div>{customer_email}</div>}
                </div>
              </>
            )}
          </div>
          <div>
            {job_title && (
              <>
                <div className="font-bold tracking-wide mb-1" style={{ color: t.primary }}>JOB TITLE</div>
                <div style={{ color: t.ink }} className="leading-snug">{job_title}</div>
              </>
            )}
          </div>
        </div>

        {/* table */}
        <div className="mt-5 flex-1" style={{ fontSize: "clamp(9px,2vw,12px)" }}>
          <div
            className={`flex items-center justify-between pb-1 font-bold tracking-wide ${serifLabel}`}
            style={{ color: t.primary, borderBottom: `1px solid ${t.hair}`, borderTop: `1px solid ${t.hair}`, paddingTop: 4 }}
          >
            <span>DESCRIPTION</span>
            <span>AMOUNT</span>
          </div>
          {lines.length === 0 && (
            <div className="py-3 italic" style={{ color: t.gray }}>Add line items to build the quote…</div>
          )}
          {lines.map((l, i) => (
            <div key={i} className="flex items-start justify-between py-2 gap-3" style={{ borderBottom: `1px solid ${t.row}`, color: t.ink }}>
              <span className="flex-1">{l.description || "—"}</span>
              <span className="font-mono shrink-0">{money(l.amount)}</span>
            </div>
          ))}

          {hasTax && (
            <div className="mt-2 space-y-1" style={{ color: t.ink }}>
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-mono">{money(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Tax ({Number(tax_rate)}%)</span>
                <span className="font-mono">{money(taxAmt)}</span>
              </div>
            </div>
          )}
        </div>

        {/* total bar */}
        {img ? (
          <div data-testid="template-total-art" className="mt-3 flex items-center justify-between font-serif font-bold" style={{ backgroundImage: `url(${img.bar})`, backgroundSize: "100% 100%", aspectRatio: "504 / 60", color: t.barText, padding: "0 5%" }}>
            <span className="tracking-wide" style={{ fontSize: "clamp(12px,2.8vw,20px)" }}>TOTAL</span>
            <span style={{ fontSize: "clamp(12px,2.8vw,20px)" }}>{money(total)}</span>
          </div>
        ) : (
          <div className="mt-3 flex items-center justify-between px-3 py-2 font-bold" style={{ backgroundColor: t.pale, color: t.barText }}>
            <span className="tracking-wide" style={{ fontSize: "clamp(10px,2.2vw,14px)" }}>TOTAL</span>
            <span className="font-serif" style={{ fontSize: "clamp(12px,2.6vw,18px)" }}>{money(total)}</span>
          </div>
        )}

        {/* footer */}
        <div className={`mt-3 italic leading-snug ${serifLabel}`} style={{ color: t.gray, fontSize: "clamp(8px,1.8vw,11px)" }}>
          {note && <div className="mb-1">Note: {note}</div>}
          <div>Valid for 30 days from date of quote</div>
        </div>
      </div>
    </div>
  );
};
